import React, { Component } from "react";
import { createPortal } from "react-dom";
import style from "./style.module.css";
import DuplicatePageIcon from "views/components/icons/DuplicatePageIcon";
import PasteIcon from "views/components/icons/PasteIcon";
import StyleBrushIcon from "views/components/icons/StyleBrushIcon";
import PositionFilledIcon from "views/components/icons/PositionFilledIcon";
import PositionIcon from "views/components/icons/PositionIcon";
import ReloadIcon from "views/components/icons/ReloadIcon";
import DiagonalBarsIcon from "views/components/icons/DiagonalBarsIcon";
import GarbageCanIcon from "views/components/icons/GarbageCanIcon";
import PadlockIcon from "views/components/icons/PadlockIcon";
import PadlockOpenSmoothIcon from "views/components/icons/PadlockOpenSmoothIcon";
import { FillFrameIcon, FitFrameIcon } from "../icons";
import CellLockingIcon from "views/components/icons/CellLockingIcon";
import SparklesIcon from "views/components/icons/SparklesIcon";
import MergeIcon from "views/components/icons/MergeIcon";
import GroupIcon from "views/components/icons/GroupIcon";
import UngroupIcon from "views/components/icons/UngroupIcon";
import SelectGroupIcon from "views/components/icons/SelectGroupIcon";
import RemoveFromGroupIcon from "views/components/icons/RemoveFromGroupIcon";
import DocumentTextIcon from "views/components/icons/DocumentTextIcon";
import { TABLE_2_RESTRICTIONS } from "lib/constants";
import { MERGE_BLOCKING_RESTRICTIONS } from "lib/textBoxMergeUtils";
import { getSelectAllTypeInfo } from "./getSelectAllTypeInfo";
import ContextMenuSubmenu from "views/components/ContextMenuSubmenu/ContextMenuSubmenu";
import LayersIcon from "views/components/icons/LayersIcon";
import { getElementIcon } from "views/components/Editor/sidebar/tabs/layers/getElementIcon";
import { getElementLabel } from "./getElementLabel";

export default class ContextMenu extends Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.selectedItemsWithRestrictions =
      this.selectedItemsWithRestrictions.bind(this);
    this.getSelectedItemsRestrictions =
      this.getSelectedItemsRestrictions.bind(this);
    this.onLockClick = this.onLockClick.bind(this);
    this.state = {
      hasRestriction: false
    };
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleOutsideClick);
    this.handleReposition();
    this.getSelectedItemsRestrictions();
  }

  componentDidUpdate(prevProps) {
    this.handleReposition();
    if (prevProps.designData !== this.props.designData) {
      this.getSelectedItemsRestrictions();
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  }

  handleOutsideClick(event) {
    let { target } = event;
    let isOutside = true;

    while (target && this.ref) {
      if (target === this.ref.current) {
        isOutside = false;
        break;
      }
      target = target.parentNode;
    }

    if (isOutside) {
      this.props.onClose(event);
    }
  }

  handleReposition() {
    const { current: ref } = this.ref || {};

    if (ref) {
      // Reset all inline styles we may have written previously so each
      // reposition starts from a clean slate.
      ref.style.transform = "";
      ref.style.maxHeight = "";
      ref.style.overflowY = "";

      const rect = ref.getBoundingClientRect();

      // The visible bottom of the editor is occluded by the fixed Timeline
      // (z-index 99) when present, while this menu sits at z-index 10. Clamp
      // the menu's bottom edge to the timeline's top edge (with a small gap)
      // so the menu never disappears behind the timeline.
      const TIMELINE_GAP = 8;
      const timelineEl = document.querySelector("[data-editor-timeline]");
      const timelineRect = timelineEl
        ? timelineEl.getBoundingClientRect()
        : null;
      // Guard against unusual viewports (e.g. timeline pinned to the top, or
      // jsdom returning top: 0): a non-positive maxBottom would otherwise
      // produce a negative max-height that the browser rejects, leaving the
      // menu invisible — the very thing this clamp is meant to prevent.
      const maxBottom = timelineRect
        ? Math.max(
            0,
            Math.min(window.innerHeight, timelineRect.top - TIMELINE_GAP)
          )
        : window.innerHeight;

      let offsetX = 0;
      let offsetY = 0;

      if (rect.left < 0) {
        offsetX += 0 - rect.left;
      }

      if (rect.top + rect.height > maxBottom) {
        offsetY += maxBottom - (rect.top + rect.height);
      }

      if (rect.left + rect.width > window.innerWidth) {
        offsetX += window.innerWidth - (rect.left + rect.width);
      }

      // Top-edge clamp: if the bottom-clamp pushed the top above the
      // viewport, pin the top to y = 0 so the first items remain visible.
      if (rect.top + offsetY < 0) {
        offsetY = -rect.top;
      }

      // Scroll fallback: if the menu is still taller than the available
      // vertical space, give it a max-height + internal scroll so every
      // item stays reachable. Skip when maxBottom is 0 — a 0px max-height
      // would just hide the menu.
      if (maxBottom > 0 && rect.height > maxBottom) {
        ref.style.maxHeight = `${maxBottom}px`;
        ref.style.overflowY = "auto";
        offsetY = -rect.top;
      }

      ref.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
    }
  }

  selectedItemsWithRestrictions = () =>
    this.props.selectedItems.map(item => {
      return this.props.designData.getElementWithRestrictions(
        Object.assign({}, item, { uniqueId: item.itemId })
      );
    });

  getSelectedItemsRestrictions = () => {
    const restrictedSelectedItems = this.selectedItemsWithRestrictions();

    const restrictions = restrictedSelectedItems.map(item => item.restrictions);
    if (
      restrictions.some(
        element => element.sizeAndPosition === true || element.position === true
      )
    ) {
      this.setState({ hasRestriction: true });
    }
  };

  onLockClick() {
    this.props.onElementsRestrictionChange({
      attribute: "sizeAndPosition",
      value: !this.state.hasRestriction
    });

    this.setState({ hasRestriction: !this.state.hasRestriction });
  }

  render() {
    const {
      clientX,
      clientY,
      enable,
      offsetX = 0 - 10,
      // Menu is now `position: fixed`, so offsets are in viewport coordinates.
      // The previous default subtracted the 60px editor header that
      // `position: absolute` inside `.editorBody` introduced.
      offsetY = 0 - 10,
      onClick
    } = this.props;

    const getColor = isEnabled =>
      isEnabled ? "var(--color-easil-gray-x-dark)" : "var(--color-easil-gray)";

    // selection (Redux) can lag designData (local state); element may be absent
    const singleSelectedElement =
      this.props.selectedItems.length === 1
        ? this.props.designData.elements[this.props.selectedItems[0].itemId]
        : undefined;

    const isImageElement = singleSelectedElement?.type === "image";

    const isFitToFrame = !!singleSelectedElement?.fitToFrame;

    const Icon = !isFitToFrame ? FitFrameIcon : FillFrameIcon;
    const label = !isFitToFrame ? "Fit to Frame" : "Fill Frame";

    let isFitToFrameDisabled,
      showTableCellLocking,
      showDocumentTextMode,
      showMergeTables,
      showMergeTextBoxes;
    if (singleSelectedElement) {
      isFitToFrameDisabled =
        singleSelectedElement.restrictions.includes("cropping");
      showTableCellLocking =
        singleSelectedElement.type === "table2" && this.props.isDesigner;
      showDocumentTextMode =
        singleSelectedElement.type === "textbox" &&
        this.props.isDesigner &&
        !this.props.isBasicPlan &&
        this.props.canUseDocumentSmartText !== false;
    }

    // Show merge tables option when 2 or more Table2 elements are selected
    if (this.props.selectedItems.length >= 2) {
      const selectedElements = this.props.selectedItems.map(
        item => this.props.designData.elements[item.itemId]
      );
      const allTable2 = selectedElements.every(
        element => element && element.type === "table2"
      );

      // Check if any table has row or column layout restrictions
      const hasLayoutRestrictions = selectedElements.some(
        element =>
          element &&
          element.restrictions &&
          element.restrictions.some(restriction =>
            [
              TABLE_2_RESTRICTIONS.EDIT_ROW_LAYOUT,
              TABLE_2_RESTRICTIONS.EDIT_COLUMN_LAYOUT
            ].includes(restriction)
          )
      );

      showMergeTables = allTable2 && !hasLayoutRestrictions;

      const allTextBox = selectedElements.every(
        element => element && element.type === "textbox"
      );
      const hasTextBoxBlockingRestriction = selectedElements.some(
        element =>
          element &&
          element.restrictions &&
          element.restrictions.some(restriction =>
            MERGE_BLOCKING_RESTRICTIONS.includes(restriction)
          )
      );
      showMergeTextBoxes = allTextBox && !hasTextBoxBlockingRestriction;
    }

    // Determine if "Select All [Type]" should be shown
    const { showSelectAll, SelectAllIcon, selectAllLabel } =
      getSelectAllTypeInfo(
        this.props.selectedItems,
        this.props.designData,
        this.props.currentPageId
      );

    return createPortal(
      <ul
        className={style.contextMenuContainer}
        data-testid="ContextMenu"
        ref={this.ref}
        style={{
          left: `${clientX + offsetX}px`,
          top: `${clientY + offsetY}px`
        }}
      >
        <li disabled={!enable.copy} onClick={e => onClick(e, "copy")}>
          <DuplicatePageIcon color={getColor(enable.copy)} />
          Copy
        </li>
        <li disabled={!enable.copyStyle} onClick={e => onClick(e, "copyStyle")}>
          <StyleBrushIcon variant="copy" color={getColor(enable.copyStyle)} />
          Copy Style
        </li>
        <li disabled={!enable.paste} onClick={e => onClick(e, "paste")}>
          <PasteIcon color={getColor(enable.paste)} />
          Paste
        </li>
        <li
          disabled={!enable.pasteStyle}
          onClick={e => onClick(e, "pasteStyle")}
        >
          <StyleBrushIcon variant="paste" color={getColor(enable.pasteStyle)} />
          Paste Style
        </li>
        <li disabled={!enable.delete} onClick={e => onClick(e, "delete")}>
          <GarbageCanIcon color={getColor(enable.delete)} />
          Delete
        </li>
        {enable.group && (
          <li onClick={e => onClick(e, "group")}>
            <GroupIcon color={getColor(true)} />
            Group
          </li>
        )}
        {enable.ungroup && (
          <li onClick={e => onClick(e, "ungroup")}>
            <UngroupIcon color={getColor(true)} />
            Ungroup
          </li>
        )}
        {enable.removeFromGroup && (
          <li onClick={e => onClick(e, "removeFromGroup")}>
            <RemoveFromGroupIcon color={getColor(true)} />
            Remove from group
          </li>
        )}
        {enable.selectGroup && (
          <li onClick={e => onClick(e, "selectGroup")}>
            <SelectGroupIcon color={getColor(true)} />
            Select Group
          </li>
        )}
        <hr />
        <li
          disabled={!enable.bringToFront}
          onClick={e => onClick(e, "moveToTop")}
        >
          <PositionFilledIcon color={getColor(enable.bringToFront)} />
          Bring to Front
        </li>
        <li disabled={!enable.bringForward} onClick={e => onClick(e, "moveUp")}>
          <PositionFilledIcon color={getColor(enable.bringForward)} />
          Bring Forward
        </li>
        <li
          disabled={!enable.sendBackward}
          onClick={e => onClick(e, "moveDown")}
        >
          <PositionIcon color={getColor(enable.sendBackward)} />
          Send Backward
        </li>
        <li
          disabled={!enable.sendToBack}
          onClick={e => onClick(e, "moveToBottom")}
        >
          <PositionIcon color={getColor(enable.sendToBack)} />
          Send to Back
        </li>
        {this.props.overlappingElements?.length > 1 && (
          <>
            <hr />
            <ContextMenuSubmenu
              parentOption={{
                key: "selectLayer",
                label: "Select Layer",
                icon: <LayersIcon color="var(--color-easil-gray-x-dark)" />
              }}
              submenuOptions={this.props.overlappingElements.map(
                ({ element, groupId }) => {
                  const Icon = getElementIcon(element.type);
                  const isSelected = this.props.selectedItems.some(
                    item => item.itemId === element.uniqueId
                  );
                  return {
                    key: element.uniqueId,
                    label: getElementLabel(element),
                    icon: <Icon color="var(--color-easil-gray-x-dark)" />,
                    isActive: isSelected
                  };
                }
              )}
              onOptionClick={key => onClick(null, "selectLayer:" + key)}
            />
          </>
        )}
        {enable.setBackground && (
          <>
            <hr />
            {isImageElement && (
              <li
                onClick={e => onClick(e, "toggleFitToFrame")}
                data-testid="fitFrameContextMenu"
                disabled={isFitToFrameDisabled}
              >
                <Icon color={getColor(!isFitToFrameDisabled)} />
                {label}
              </li>
            )}
            <li onClick={e => onClick(e, "setAsBackground")}>
              <DiagonalBarsIcon color={getColor(enable.setBackground)} />
              Set as Background
            </li>
            {enable.aiBackgroundRemove && (
              <li onClick={e => onClick(e, "aiBackgroundRemove")}>
                <SparklesIcon color={getColor(enable.aiBackgroundRemove)} />
                AI Background Erase
              </li>
            )}
          </>
        )}
        {enable.updateCollection && (
          <>
            <hr />
            <li onClick={e => onClick(e, "updateCollection")}>
              <ReloadIcon color={getColor(enable.updateCollection)} />
              Update Collection
            </li>
          </>
        )}
        {showDocumentTextMode && (
          <>
            <hr />
            <li
              onClick={e => onClick(e, "documentTextMode")}
              data-testid="ContextMenu-documentTextMode"
            >
              <DocumentTextIcon color={"#57595D"} />
              Create Smart Data
            </li>
          </>
        )}
        {!this.props.isBasicPlan &&
          this.props.isDesigner &&
          !this.props.selectedItems.length <= 0 && (
            <>
              {!showDocumentTextMode && <hr />}
              <li onClick={this.onLockClick}>
                {this.state.hasRestriction ? (
                  <>
                    <PadlockOpenSmoothIcon color={"#57595D"} />
                    Unlock Position
                  </>
                ) : (
                  <>
                    <PadlockIcon color={"#57595D"} />
                    Lock Position
                  </>
                )}
              </li>
            </>
          )}
        {showTableCellLocking && (
          <li onClick={e => onClick(e, "cellLockingMode")}>
            <CellLockingIcon color={"#57595D"} />
            Cell Locking Mode
          </li>
        )}
        {showMergeTables && (
          <>
            <hr />
            <li onClick={e => onClick(e, "mergeTables")}>
              <MergeIcon color={"#57595D"} />
              Merge Tables
            </li>
          </>
        )}
        {showMergeTextBoxes && (
          <>
            <hr />
            <li onClick={e => onClick(e, "mergeTextBoxes")}>
              <MergeIcon color={"#57595D"} />
              Merge Text Boxes
            </li>
          </>
        )}
        {showSelectAll && (
          <>
            <hr />
            <li onClick={e => onClick(e, "selectAllSameType")}>
              <SelectAllIcon color={"#57595D"} />
              {selectAllLabel}
            </li>
          </>
        )}
      </ul>,
      document.body
    );
  }
}
